// src/pages/admin/tabs/CurrenciesTab.jsx
import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Row3, Field, Actions, Btn } from '../ui';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // локально читаемо: YYYY-MM-DD HH:mm
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export default function CurrenciesTab(){
  // храню только интересующие поля ввода
  const [rates, setRates] = React.useState({ USD:'', EUR:'', AED:'', RUB:'' });

  // мета с бэка
  const [base, setBase] = React.useState('AED');
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [status, setStatus] = React.useState(null);

  const [ok, setOk] = React.useState('');
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [autoUpdating, setAutoUpdating] = React.useState(false);

  const fetchRates = React.useCallback(async ()=>{
    setLoading(true); setErr(''); setOk('');
    try {
      // подтянем все активные валюты
      const data = await AdminAPI.listCurrencyRates(); // GET /admin/currencies/rates
      const cur = Array.isArray(data?.currencies) ? data.currencies : [];
      const byCode = {};
      for (const c of cur) {
        if (!c?.code) continue;
        byCode[c.code.toUpperCase()] = c;
      }
      setRates({
        USD: byCode.USD?.rate_to_base != null ? String(byCode.USD.rate_to_base) : '',
        EUR: byCode.EUR?.rate_to_base != null ? String(byCode.EUR.rate_to_base) : '',
        AED: byCode.AED?.rate_to_base != null ? String(byCode.AED.rate_to_base) : '',
        RUB: byCode.RUB?.rate_to_base != null ? String(byCode.RUB.rate_to_base) : '',
      });
      setBase(data?.base || 'AED');
      setUpdatedAt(data?.updated_at || null);
    } catch (e) {
      setErr(e.message || 'Не удалось загрузить курсы');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatus = React.useCallback(async () => {
    try {
      const statusData = await AdminAPI.getCurrencyStatus(); // GET /admin/currencies/status
      setStatus(statusData);
    } catch (e) {
      console.error('Failed to fetch currency status:', e);
    }
  }, []);

  React.useEffect(()=>{ 
    fetchRates(); 
    fetchStatus();
  }, [fetchRates, fetchStatus]);

  const save = async () => {
    setErr(''); setOk(''); setSaving(true);
    try {
      // собираем только заполненные поля
      const payloadArr = [];
      if (rates.USD !== '') payloadArr.push({ code: 'USD', rate_to_base: Number(rates.USD) });
      if (rates.EUR !== '') payloadArr.push({ code: 'EUR', rate_to_base: Number(rates.EUR) });
      if (rates.AED !== '') payloadArr.push({ code: 'AED', rate_to_base: Number(rates.AED) });
      if (rates.RUB !== '') payloadArr.push({ code: 'RUB', rate_to_base: Number(rates.RUB) });

      if (!payloadArr.length) {
        setErr('Заполните хотя бы одно значение');
        setSaving(false);
        return;
      }

      await AdminAPI.updateRates(payloadArr); // PUT /admin/currencies/rates  c body: { currencies: [...] }
      setOk('Курсы обновлены вручную');
      await fetchRates(); // рефетчим, чтобы показать свежие значения и updated_at
      await fetchStatus();
    } catch (e){
      setErr(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const autoUpdate = async (force = false) => {
    setErr(''); setOk(''); setAutoUpdating(true);
    try {
      const result = await AdminAPI.autoUpdateCurrencyRates({ force }); // POST /admin/currencies/auto-update
      
      if (result.success) {
        if (result.updated_currencies && result.updated_currencies.length > 0) {
          setOk(`Автоматически обновлено ${result.updated_currencies.length} валют с внешних API`);
        } else {
          setOk('Курсы актуальны, обновление не требуется');
        }
        await fetchRates();
        await fetchStatus();
      } else {
        setErr(result.errors?.join(', ') || 'Ошибка автоматического обновления');
      }
    } catch (e) {
      setErr(e.message || 'Ошибка автоматического обновления');
    } finally {
      setAutoUpdating(false);
    }
  };

  return (
    <>
      <Card>
        <h3>Курсы валют (относительно базовой)</h3>
        <p style={{opacity:.8, marginTop:4, marginBottom:8}}>
          Базовая валюта: <b>{base || 'AED'}</b> • Обновлено: <b>{fmtDate(updatedAt)}</b>
        </p>
        <p style={{opacity:.8, marginTop:0, marginBottom:12}}>
          Курсы можно обновлять вручную или автоматически с внешних API. 
          Базовая валюта ({base}) всегда имеет курс 1.0.
        </p>

        {loading ? (
          <div>Загрузка…</div>
        ) : (
          <>
            <Row3>
              <Field>USD (к {base})
                <input
                  type="number" step="0.0001"
                  value={rates.USD}
                  onChange={e=>setRates({...rates, USD:e.target.value})}
                  placeholder="напр. 3.6700"
                />
              </Field>
              <Field>EUR (к {base})
                <input
                  type="number" step="0.0001"
                  value={rates.EUR}
                  onChange={e=>setRates({...rates, EUR:e.target.value})}
                  placeholder="напр. 4.0200"
                />
              </Field>
              <Field>RUB (к {base})
                <input
                  type="number" step="0.0001"
                  value={rates.RUB}
                  onChange={e=>setRates({...rates, RUB:e.target.value})}
                  placeholder="напр. 0.0367"
                />
              </Field>
            </Row3>

            <Actions style={{marginTop:10}}>
              <Btn $kind="primary" onClick={save} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить вручную'}
              </Btn>
              <Btn $kind="secondary" onClick={() => autoUpdate(false)} disabled={autoUpdating}>
                {autoUpdating ? 'Обновление…' : 'Обновить автоматически'}
              </Btn>
              <Btn onClick={() => autoUpdate(true)} disabled={autoUpdating}>
                Принудительно обновить
              </Btn>
              <Btn onClick={fetchRates} disabled={loading}>Перезагрузить</Btn>
            </Actions>

            {ok && <div style={{color:'#2a8f2a', marginTop:8, padding:8, background:'#f1f8e9', borderRadius:4}}>{ok}</div>}
            {err && <div style={{color:'#e66', marginTop:8, padding:8, background:'#ffebee', borderRadius:4}}>{err}</div>}
          </>
        )}
      </Card>

      {status && (
        <Card style={{marginTop:16}}>
          <h3>Статус автоматического обновления</h3>
          <div style={{display:'grid', gap:8, marginTop:8}}>
            <div><strong>Всего валют:</strong> {status.total_currencies}</div>
            <div><strong>Базовая валюта:</strong> {status.base_currency}</div>
            <div><strong>Последнее обновление:</strong> {fmtDate(status.last_update)}</div>
          </div>
          
          {status.currencies && status.currencies.length > 0 && (
            <div style={{marginTop:12}}>
              <h4>Текущие курсы:</h4>
              <div style={{display:'grid', gap:4, fontSize:14}}>
                {status.currencies.map(currency => (
                  <div key={currency.code} style={{display:'flex', justifyContent:'space-between', padding:4, background:'#f8f9fa', borderRadius:4}}>
                    <span><strong>{currency.code}</strong> ({currency.name})</span>
                    <span>{currency.rate_to_base}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{marginTop:12, padding:8, background:'#e3f2fd', borderRadius:4, fontSize:13}}>
            <strong>Автоматическое обновление:</strong> Курсы обновляются каждый час автоматически через cron задачу. 
            Используются бесплатные API: exchangerate-api.com, fixer.io, currencylayer.com
          </div>
        </Card>
      )}

      <Card style={{marginTop:16}}>
        <h3>API Endpoints</h3>
        <div style={{fontSize:13, opacity:0.7, fontFamily:'monospace'}}>
          <div>GET /admin/currencies/rates - получить курсы</div>
          <div>PUT /admin/currencies/rates - обновить курсы вручную</div>
          <div>POST /admin/currencies/auto-update - автоматическое обновление</div>
          <div>GET /admin/currencies/status - статус валют</div>
        </div>
      </Card>
    </>
  );
}
